import React, { useState, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import BottomSheet from './BottomSheet';
import '../styles/components/insurancePdfBottomSheet.css';

interface InsurancePdfBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (name: string, email: string) => void;
  userName: string;
  userEmail: string;
  isLoading?: boolean;
}

const InsurancePdfBottomSheet: React.FC<InsurancePdfBottomSheetProps> = ({
  isOpen,
  onClose,
  onSend,
  userName,
  userEmail,
  isLoading = false,
}) => {
  const [name, setName] = useState(userName);
  const [email, setEmail] = useState(userEmail);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const isIOSNative = Capacitor.getPlatform() === 'ios';

  // Update fields when user data changes
  useEffect(() => {
    setName(userName);
    setEmail(userEmail);
  }, [userName, userEmail]);

  // Handle iOS keyboard showing/hiding using Capacitor Keyboard plugin
  useEffect(() => {
    if (!isOpen || !isIOSNative) return;

    const { Keyboard } = require('@capacitor/keyboard');

    const showListener = Keyboard.addListener('keyboardWillShow', (info: any) => {
      setKeyboardVisible(true);
    });

    const hideListener = Keyboard.addListener('keyboardWillHide', () => {
      setKeyboardVisible(false);
    });

    return () => {
      showListener.then((l: any) => l.remove());
      hideListener.then((l: any) => l.remove());
      setKeyboardVisible(false);
    };
  }, [isOpen, isIOSNative]);

  const handleSend = () => {
    if (!name.trim() || !email.trim()) {
      return;
    }
    onSend(name.trim(), email.trim());
  };

  const handleCancel = () => {
    // Reset to original values
    setName(userName);
    setEmail(userEmail);
    onClose();
  };

  return (
    <BottomSheet 
      isOpen={isOpen} 
      onClose={handleCancel} 
      title="Insurance PDF"
      className={keyboardVisible ? 'keyboard-visible' : ''}
    >
      <div className="insurance-pdf-form">
        <div className="insurance-pdf-field">
          <label htmlFor="insurance-name">Name</label>
          <input
            ref={nameInputRef}
            id="insurance-name"
            type="text"
            className="insurance-pdf-input"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isLoading}
          />
        </div>

        <div className="insurance-pdf-field">
          <label htmlFor="insurance-email">Email</label>
          <input
            ref={emailInputRef}
            id="insurance-email"
            type="email"
            className="insurance-pdf-input"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
          />
        </div>

        <div className="insurance-pdf-actions">
          <button
            className="insurance-pdf-btn cancel-btn"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            className="insurance-pdf-btn send-btn"
            onClick={handleSend}
            disabled={isLoading || !name.trim() || !email.trim()}
          >
            {isLoading ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
};

export default InsurancePdfBottomSheet;
